import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RawDraftContentState } from 'draft-js';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getWorldGeometryJSON } from '../../home/store';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../shared/tooltip';
import styles from './NettingEngine.module.scss';
import { NettingEngineAnswerJustificationModal } from './NettingEngineAnswerJustificationModal';
import { NettingEngineConditionModal } from './NettingEngineConditionModal';
import { NettingEngineEnquireOverlay } from './NettingEngineEnquireOverlay';
import { NettingEngineMapOutline } from './NettingEngineMapOutline';
import { ISDAJurisdictionalNettingInformation, getSelectedProductCovered, getAnswerJustificationModalOpen, setAnswerJustificationModalOpen, getSelectedJurisdictionDetails, getNettingEngineAgreementType, GMRAJurisdictionalNettingInformation, JurisdictionalNettingDetailsAnswer, setSelectedJurisdictionDetails, JurisdictionalAnswers } from './store';
import { getCountryGeometry } from './store/utils';
import { CloseOutNettingSummary, CollateralisationMethodSummary } from '../store/types';
import { ISDANettingEngineJurisdictionAnswers } from './ISDANettingEngineJurisdictionAnswers';
import { GMRANettingEngineJurisdictionAnswers } from './GMRANettingEngineJurisdictionAnswers';
import { NettingEngineJurisdictionProductsCovered } from './NettingEngineJurisdictionProductsCovered';
import { IconButton } from '../../shared/button/IconButton';
import { SmileyNegative, SmileyNeutral, SmileyPositive } from '../../shared/icons';
import { NettingEngineJurisdictionSummary } from './NettingEngineJurisdictionSummary';
import { AgreementType } from '../../admin/documents/store/types';

const { green, red, grey, amber, yellowGreen, fixedPrimary } = styles;

interface NettingEngineJurisdictionResultsProps {
    jurisdictionalResults: ISDAJurisdictionalNettingInformation | GMRAJurisdictionalNettingInformation;
    viewCustomConclusions: boolean;
    height: number;
    width: number;
    isLoading: boolean;
    index: number;
    isInstanceView: boolean;
    isSingleJurisdiction: boolean;
}

const PADDING = 20;
const MARGIN = 20;

const getIsdaMapFillColour = (summaryValue: CloseOutNettingSummary) => {
    switch (summaryValue) {
        case CloseOutNettingSummary.YES: return green;
        case CloseOutNettingSummary.YES_PRODUCTS_UNKNOWN: return yellowGreen;
        case CloseOutNettingSummary.NO: return red;
        case CloseOutNettingSummary.MAYBE: return amber;
        default: return grey;
    }
};

const getGMRAMapFillColour = (summaryValue: CloseOutNettingSummary, collateralSummary: CollateralisationMethodSummary) => {
    let colour = grey;
    if (summaryValue === CloseOutNettingSummary.YES && collateralSummary === CollateralisationMethodSummary.YES) colour = green;
    if (summaryValue === CloseOutNettingSummary.YES_PRODUCTS_UNKNOWN && collateralSummary === CollateralisationMethodSummary.YES) colour = yellowGreen;
    if (summaryValue === CloseOutNettingSummary.NO || collateralSummary === CollateralisationMethodSummary.NO) colour = red;
    if (summaryValue === CloseOutNettingSummary.MAYBE || collateralSummary === CollateralisationMethodSummary.MAYBE) colour = amber;
    return colour;
};

export const NettingEngineJurisdictionResults: React.FC<NettingEngineJurisdictionResultsProps> = ({ jurisdictionalResults, viewCustomConclusions, height, width, isLoading, index, isInstanceView, isSingleJurisdiction }) => {
    const [transactionConditionModalOpen, setTransactionConditionModalOpen] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const selectedProductCovered = useAppSelector(getSelectedProductCovered);
    const worldGeometry = useAppSelector(getWorldGeometryJSON);
    const answerJustificationModalOpen = useAppSelector(getAnswerJustificationModalOpen);
    const selectedSmileyIconDetails = useAppSelector(getSelectedJurisdictionDetails);
    const selectedAgreementType = useAppSelector(getNettingEngineAgreementType);

    const {
        permittedToView,
        jurisdiction,
        counterpartyOffices,
        summary,
        clientSignOffDetails,
        clientSignOffSummary
    } = jurisdictionalResults;

    const country = getCountryGeometry(worldGeometry, jurisdiction);

    const customConclusionsExist = useMemo(() => !isNull(clientSignOffDetails) && viewCustomConclusions, [clientSignOffDetails, viewCustomConclusions]);

    const summaryValue = useMemo(() => customConclusionsExist ? clientSignOffSummary : summary, [customConclusionsExist, clientSignOffSummary, summary]);

    const fillColour = useMemo(() => {
        if (!permittedToView) {
            return grey;
        }
        if (selectedAgreementType === AgreementType.GMRA) {
            const { collateralSummary, clientSignOffCollateralSummary } = jurisdictionalResults as GMRAJurisdictionalNettingInformation;
            const collateralSummaryValue = customConclusionsExist ? clientSignOffCollateralSummary : collateralSummary;
            return getGMRAMapFillColour(summaryValue, collateralSummaryValue);
        }
        return getIsdaMapFillColour(summaryValue);
    }, [customConclusionsExist, jurisdictionalResults, permittedToView, selectedAgreementType, summaryValue]);

    const svgHeight = useMemo(() => height - 30, [height]);
    const svgWidth = useMemo(() => width * 0.45, [width]);

    const leftHandComponent = useMemo(() => !country ? <div className={styles.jurisdictionWrapper}>{jurisdiction}</div> : <NettingEngineMapOutline country={country} fillColour={fillColour} svgHeight={svgHeight} svgWidth={svgWidth} jurisdiction={jurisdiction} />, [jurisdiction, country, fillColour, svgHeight, svgWidth]);

    const onCloseAnswerJustificationModal = useCallback(() => dispatch(setAnswerJustificationModalOpen(false)), [dispatch]);

    const left = (width + 20) * index + 'px';

    const noClientSignOffDetailsMessage = useMemo(() => isNull(clientSignOffDetails) && viewCustomConclusions ? 'Because there are no sign off details around this jurisdiction, you are still viewing system data' : null, [clientSignOffDetails, viewCustomConclusions]);

    const wrapperWidth = useMemo(() => `${width - (isSingleJurisdiction ? (PADDING + MARGIN) : 0)}px`, [width, isSingleJurisdiction]);

    const handleSmileyIconClick = useCallback((question: string, value: string, details: RawDraftContentState | null, icon: string) => {
        if (!isNull(details)) {
            dispatch(setSelectedJurisdictionDetails({ question, value, details, icon }));
            dispatch(setAnswerJustificationModalOpen(true));
        }
    }, [dispatch]);

    const getIcon = useCallback((question: string, answerString: JurisdictionalNettingDetailsAnswer, noPositive = false, isLocalLaw = false, aetApplicable: string | undefined = undefined) => {
        const { value, details } = answerString;
        const overlayText = isNull(details) ? 'There is no additional information' : 'Click for more information';
        if (
            (!permittedToView && !isLocalLaw) ||
            (isLocalLaw && value === 'Not permitted to view')
        ) return <IconButton icon={SmileyNeutral} iconFill={grey} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} overlayText={overlayText} />;
        // ISDA specific edge cases
        if (question === 'Is automatic early termination required?') {
            if (value === 'Yes' && aetApplicable === 'Yes' || value === 'No') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} overlayText={overlayText} />;
            if (value === 'Yes' && !isUndefined(aetApplicable) && ['No', 'Not Applicable'].includes(aetApplicable)) return <IconButton icon={SmileyNegative} iconFill={red} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'negative')} overlayText={overlayText} />;
            return <IconButton icon={SmileyNeutral} iconFill={amber} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} overlayText={overlayText} />;
        }
        // GMRA specific edge cases
        if (question === 'Will a transfer be respected as a title transfer?') {
            if (value === 'Yes') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} overlayText={overlayText} />;
            if (value === 'No') return <IconButton icon={SmileyNeutral} iconFill={amber} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} overlayText={overlayText} />;
        }
        if (question === 'Does recharacterisation of collateral as security adversely impact enforceability?') {
            if (value === 'No' || value === 'Not applicable') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} overlayText={overlayText} />;
            if (value === 'Yes' || value === 'Yes - with exceptions') return <IconButton icon={SmileyNegative} iconFill={red} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'negative')} overlayText={overlayText} />;
        }
        // General answer cases
        if (!noPositive) {
            if (value === 'Yes' || value === 'Not relevant to head office') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} overlayText={overlayText} />;
            if (value === 'No') return <IconButton icon={SmileyNegative} iconFill={red} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'negative')} overlayText={overlayText} />;
            return <IconButton icon={SmileyNeutral} iconFill={amber} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} overlayText={overlayText} />;
        } else {
            if (value === 'No' || value === 'Not relevant to head office') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} overlayText={overlayText} />;
            if (value === 'Yes') return <IconButton icon={SmileyNegative} iconFill={red} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'negative')} overlayText={overlayText} />;
            return <IconButton icon={SmileyNeutral} iconFill={amber} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} overlayText={overlayText} />;
        }
    }, [permittedToView, handleSmileyIconClick]);

    const getAnswerJSX = useCallback((question: string, answer: JurisdictionalNettingDetailsAnswer, noPositive = false, isLocalLaw = false, aetApplicable: string | undefined = undefined): JSX.Element => (
        <>
            <span className={styles.answerText}>{answer.value}</span>{getIcon(question, answer, noPositive, isLocalLaw, aetApplicable)}
        </>
    ), [getIcon]);

    const createQuestionAnswer = useCallback((question: string, answer: JurisdictionalNettingDetailsAnswer, noPositive = false, isLocalLaw = false, aetApplicable: string | undefined = undefined): JurisdictionalAnswers => ({ question, answer: getAnswerJSX(question, answer, noPositive, isLocalLaw, aetApplicable) }), [getAnswerJSX]);

    useEffect(() => () => {
        dispatch(setAnswerJustificationModalOpen(false));
    }, [dispatch]);

    if (isLoading) {
        return (
            <div className={styles.nettingJurisdictionResults} style={{ height, width }}>
                <PageLoadingDots />
            </div>
        );
    }

    const offices = counterpartyOffices.map(office => office).join(', ');
    // This keeps the jurisdiction label centred above the country
    const marginLeft = `calc((45% / 2) - ${(jurisdiction.length * 10 / 2)}px)`;
    const counterpartyOfficesWidth = `calc(100% - calc((45% / 2) + ${(jurisdiction.length * 10 / 2)}px))`;

    return (
        <div className={styles.nettingJurisdictionResults} style={{ height: `${height}px`, width: wrapperWidth }}>
            <div className={styles.jurisdictionWrapper}>
                <div style={{ marginLeft: marginLeft, minWidth: 'fit-content' }}>{jurisdiction}</div>
                {counterpartyOffices.length > 0 && <div className={styles.counterpartyOfficesWrapper} style={{ maxWidth: counterpartyOfficesWidth }}>
                    <div className={styles.counterpartyOfficesLabel}> - Counterparty Offices:</div>
                    <OverflowTooltip overlayText={offices} className={styles.counterpartyOffices} />
                </div>}
            </div>
            <div className={styles.resultsWrapper}>
                <div className={styles.leftHandSummary}>
                    {leftHandComponent}
                </div>
                <div className={styles.noClientSignOffDetailsMessage}>
                    <div className={styles.message}>{noClientSignOffDetailsMessage}</div>
                    <div className={styles.rightHandSummary}>
                        <Scrollable>
                            <NettingEngineJurisdictionSummary
                                jurisdictionalResults={jurisdictionalResults}
                                viewCustomConclusions={viewCustomConclusions}
                                setTransactionConditionModalOpen={setTransactionConditionModalOpen}
                                agreementType={selectedAgreementType!}
                            />
                            {selectedAgreementType === AgreementType.ISDA ?
                                <ISDANettingEngineJurisdictionAnswers
                                    viewCustomConclusions={viewCustomConclusions}
                                    jurisdictionalResults={jurisdictionalResults as ISDAJurisdictionalNettingInformation}
                                    createQuestionAnswer={createQuestionAnswer}
                                /> :
                                <GMRANettingEngineJurisdictionAnswers
                                    viewCustomConclusions={viewCustomConclusions}
                                    jurisdictionalResults={jurisdictionalResults as GMRAJurisdictionalNettingInformation}
                                    createQuestionAnswer={createQuestionAnswer}
                                />
                            }
                            <NettingEngineJurisdictionProductsCovered
                                jurisdictionalResults={jurisdictionalResults}
                                viewCustomConclusions={viewCustomConclusions}
                                setTransactionConditionModalOpen={setTransactionConditionModalOpen}
                                getAnswerJSX={getAnswerJSX}
                            />
                        </Scrollable>
                    </div>
                </div>
            </div>
            {!permittedToView && <NettingEngineEnquireOverlay jurisdiction={jurisdiction} width={width} left={left} />}
            {!isNull(selectedProductCovered) && <NettingEngineConditionModal isOpen={transactionConditionModalOpen} closeModal={() => setTransactionConditionModalOpen(false)} productCovered={selectedProductCovered} />}
            <NettingEngineAnswerJustificationModal isOpen={answerJustificationModalOpen} answerDetails={selectedSmileyIconDetails} closeModal={onCloseAnswerJustificationModal} width={svgWidth} height={svgHeight} isInstanceView={isInstanceView} />
        </div>
    );
};
